import { AppFrameWide } from "./AppFrameWide";
import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppClassList } from "./AppClassList";
import { IComposerSent } from "./AppComposer";
import { AppReactPlace, AppReactRules } from "./AppReactRules";
import { AppIcon, AppIcons } from "./AppIcons";
import { AppMessageKeyboard, IMessageElement } from "./AppMessageKeyboard";
import { AppFocus, IFocusEl } from "./AppFocus";
import { AppMsgActionRules } from "./AppMsgActions";
import { AppNodeOf } from "./AppNodeOf";
import { AppReactPill } from "./AppLiveEvents";

// AppReactions - react to a message in the open conversation (2026-09-25, owner: "it must be
// possible to react to a message in WA Commander also"). `AppReactRules` decides; this is the act.
//
// ⭐ THE PAGE STAYS SCRIPT-FREE. The contact page is a `blob:` frame of the same origin, so the app
// attaches everything from OUTSIDE - one stylesheet and a handful of delegated listeners on the
// frame's document - exactly as `AppMediaFill` and `AppChatAppend` do.
//   - A mouse: hovering a bubble shows a small smiley (`AppIcons.React`, was ☺) beside it; clicking it
//     opens WhatsApp's row of six. A touch screen: press and hold the bubble.
//   - ⭐ Every face is OUR SVG icon (`AppIcons`, 2026-09-27), built node by node - the row, the trigger
//     and the optimistic pill (`AppIcons.BuildFaces`, the page's `AppIcons.Faces` markup exactly). The
//     Unicode emoji stays only where it is the WIRE value: `data-wac-emoji` and the pill's data.
//   - Clicking an emoji SENDS it; clicking the owner's current one takes it OFF ("").
//
// ⛔ A REACTION IS A SEND (decision 9). The ONLY thing that sends is a click on one of the row's six
// buttons, and it goes through the composer's ONE sender and ONE in-flight lock
// (`AppComposer.SendReaction` -> `/send`) - never a second transport. Nothing sends on hover, on
// opening the row, on a timer or on a page load.
// ⛔ ONE REACTION IN FLIGHT PER MESSAGE: until the first has settled, a second tap on the same
// message is refused with a sentence - two queued reactions on one message would race.
//
// ⚠ THE BADGE IS OPTIMISTIC, AND SAYS SO: the pill is redrawn at once, faded (`wac-pending`); the
// receipt settles it - sent = normal, refused = the ORIGINAL pill put back exactly, with the reason
// in the hint under the box; unconfirmed = kept, with "check your phone". The rebuilt page then
// draws the same pill from the journal (`ReactionBook`): the owner's newer reaction replaces theirs.
//
// ⚠ SHAPED BY INTERFACES (`IRx*`), so the suite drives it with a small fake document.

export interface IRxRect { left: number; top: number; right: number; bottom: number; }

export interface IRxNode {
    className: string;
    textContent: string | null;
    getAttribute(Name: string): string | null;
    setAttribute(Name: string, Value: string): void;
    removeAttribute(Name: string): void;
    appendChild(Child: IRxNode): unknown;
    removeChild(Child: IRxNode): unknown;
    querySelector(Selector: string): IRxNode | null;
    closest(Selector: string): IRxNode | null;
    getBoundingClientRect?(): IRxRect;
    /** ⛔ Positions go through the CSSOM, never `setAttribute("style")` - the CSP refuses an inline style attribute. */
    style?: IRxStyle;
}

export interface IRxStyle { left: string; top: string; }

export interface IRxView { innerWidth: number; innerHeight?: number; }

export interface IRxDoc {
    head: IRxNode | null;
    body: IRxNode | null;
    defaultView?: IRxView | null;
    createElement(Tag: string): IRxNode;
    /** For our SVG icons (`AppIcons.Build`) - built node by node, never parsed from a string. */
    createElementNS(Ns: string, Tag: string): IRxNode;
    addEventListener(Type: string, Listener: (Ev: Event) => void, Capture?: boolean): void;
}

export interface IRxFrame {
    contentDocument: IRxDoc | null;
    addEventListener(Type: string, Listener: () => void): void;
}

/** The parts of a DOM event this reads - a real `Event` fits by shape. */
export interface IRxEvent {
    target: unknown;
    detail?: number;
    relatedTarget?: unknown;
    clientX?: number;
    clientY?: number;
    pointerType?: string;
    key?: string;
    shiftKey?: boolean;
    ctrlKey?: boolean;
    altKey?: boolean;
    metaKey?: boolean;
    repeat?: boolean;
    isComposing?: boolean;
    keyCode?: number;
    preventDefault(): void;
}

/** The page half: send one reaction (the composer's sender), and say a sentence under the box. */
export interface IRxHost {
    /** "" when it went (and `Then` hears once, later), else the sentence to show. */
    React(MessageId: string, Emoji: string, Then: IComposerSent): string;
    Say(Text: string, Bad: boolean): void;
    /**
     * ⭐ (2026-10-14) Reply / Edit / Delete clicked on a bubble: the composer's MODE is set (`AppComposer.SetMode`).
     * ⛔ SENDS NOTHING. Optional: without it the row has no action buttons.
     */
    Act?(Kind: string, MessageId: string, Words: string): void;
}

/** A timer, injectable so the long press is testable without waiting. */
export interface IRxLater {
    Later(Ms: number, Fire: () => void): number;
    Cancel(Id: number): void;
}

/** One reaction on its way: the bubble, the pill it replaced (null = there was none) and the new one. */
export class AppReactPending {
    public Bubble: IRxNode | null = null;
    public OldPill: IRxNode | null = null;
    public NewPill: IRxNode | null = null;
    public OldClass: string = "";
    /** The emoji that is on its way ("" = taking the reaction off) - what its live echo is known by (`AppLiveReact`). */
    public Next: string = "";
}

export class AppReactions {
    public static readonly TriggerClass: string = "wac-rxbtn";
    public static readonly RowClass: string = "wac-rxrow";
    public static readonly PendingClass: string = "wac-pending";
    public static readonly EmojiAttr: string = "data-wac-emoji";
    /** How long a touch must be held to open the row. */
    public static readonly HoldMs: number = 450;
    /** A click this soon after a hold opened the row is the finger lifting, not a choice. */
    public static readonly LiftMs: number = 700;
    public static readonly GraceMs: number = 150;

    private Host: IRxHost;
    private Coarse: boolean;
    private Timer: IRxLater;
    private Frame: IRxFrame | null = null;
    private Doc: IRxDoc | null = null;
    private Trigger: IRxNode | null = null;
    private TriggerOn: IRxNode | null = null;
    private MouseMoved: boolean = false;
    private Grace: number = 0;
    private GraceVersion: number = 0;
    private Row: IRxNode | null = null;
    /** The bubble the row is open on, or null. */
    private Target: IRxNode | null = null;
    public readonly Latch: AppHoldLatch;
    /**
     * When a hold last opened the row (epoch ms). The click some browsers fire as the finger lifts
     * must not close it again. ⚠ A TIME, NOT A FLAG: a browser that fires NO click after a hold would
     * otherwise leave a flag set that swallows the owner's next click - on an emoji.
     */
    private HeldAt: number = 0;
    /** The row's six buttons, in `AppReactRules.Emoji`'s order. */
    private RowButtons: IRxNode[] = [];
    /** ⭐ The row's Reply / Edit / Delete buttons (`AddActs`). */
    private ActButtons: IRxNode[] = [];
    /** The row is in the page right now. */
    private Shown: boolean = false;
    /** ⭐ The bubble the row was opened on BY KEY (`r`, Menu, Shift+F10): Esc and a pick give the focus back to it. */
    private KeyOrigin: IRxNode | null = null;
    /** The clock, for `HeldAt`. A field so the suite can move it. */
    public Now: () => number = Date.now;
    private Pending: Record<string, AppReactPending> = {};
    public Stopped: boolean = false;
    /**
     * ⭐ LIVE REACTIONS (2026-10-01, `AppLiveReact`): told when a reaction on its way SETTLED - `Kept` true = it stands
     * (sent, unconfirmed, or the page stopped waiting), false = refused and the original pill is back. The live feed
     * holds other reactions to that message back until then. Null = nobody listens (as before).
     */
    public OnSettled: ((Mid: string, Kept: boolean) => void) | null = null;

    public constructor(Host: IRxHost, Coarse: boolean, Timer: IRxLater) {
        this.Host = Host;
        this.Coarse = Coarse;
        this.Timer = Timer;
        this.Latch = new AppHoldLatch(AppReactions.HoldMs, Timer);
    }

    public Attach = (Frame: IRxFrame): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Frame = Frame;
            Frame.addEventListener("load", this.OnLoad);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.Attach");
        }
    };

    /**
     * The page is in: the listeners - once per page. ⛔ NO `<style>` IS ADDED: the CSP has no 'unsafe-inline'
     * for styles, so `Css` rides in the frame's own linked sheet (`AppMain.Frame` -> `AppFrameSheet`).
     */
    public OnLoad = (): void => {
        AppDocListen.Loaded(this.Stopped, this.Frame, this.Doc !== null, this.OnLoaded, "AppReactions.OnLoad");
    };

    /** The reaction row's own per-document load: its listeners, its keyboard attach. */
    private OnLoaded = (Raw: unknown): void => {
        const Doc: IRxDoc = Raw as IRxDoc;
        this.Doc = Doc;
        AppDocListen.Pointer(Doc, this.OnOver, this.OnMove, this.OnOut, this.OnDown, this.OnUp);
        Doc.addEventListener("click", this.OnClick);
        Doc.addEventListener("contextmenu", this.OnMenu);
        Doc.addEventListener("keydown", this.OnKey);
        Doc.addEventListener("scroll", this.OnScroll, true);
        AppMessageKeyboard.Attach(Doc, this.OnKeyboardOpen, this.OnKeyboardClose);
    };

    /** The frame is being replaced: nothing more is drawn, and a late receipt changes nothing. */
    public Stop = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Stopped = true;
            this.CancelHold();
            this.Close();
            this.RestTrigger();
            this.Pending = {};
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.Stop");
        }
    };

    // ── Opening the row ──────────────────────────────────────────────────────────────────────

    /** The offerable bubble an event is on, or null. */
    public BubbleOf = (Target: unknown): IRxNode | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Node: IRxNode | null = AppReactions.NodeOf(Target);
            if (Node === null) { return null; }
            const Bubble: IRxNode | null = Node.closest(".b");
            if (Bubble === null) { return null; }
            if (AppReactRules.Offer(Bubble.getAttribute("data-mid"), Bubble.getAttribute("data-wac-id")) === false) { return null; }
            return Bubble;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.BubbleOf");
            return null;
        }
    };

    /** A mouse over an offerable bubble: the ☺ moves onto it. ⚠ Not on a touch screen. */
    public OnOver = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true || this.Coarse === true || this.Doc === null || this.MouseMoved === false) { return; }
            const Bubble: IRxNode | null = this.BubbleOf((Ev as unknown as IRxEvent).target);
            if (Bubble === null) {
                if (this.InGutter(Ev) === true) { this.CancelGrace(); }
                else { this.LeaveSoon(); }
                return;
            }
            this.ShowTrigger(Bubble);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.OnOver");
        }
    };

    private ShowTrigger = (Bubble: IRxNode): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Doc === null) { return; }
            this.CancelGrace();
            // ⛔ NEVER MOVED WHILE IT IS ALREADY ON THIS BUBBLE (P0 found by the audit, 2026-10-12). Every `mouseover` -
            // including the pointer crossing onto the opener itself - re-appended the opener, i.e. took the element
            // under the pointer out of the page and put it back. Chrome then dispatches NO `click` for a press that
            // began on it: the opener showed, and pressing it did nothing (reproduced on the shipped 1.1.4.0 bundle
            // with `capture-screens.mjs ONLY=reactions`; a mouse user could not react at all).
            if (Bubble.querySelector("." + AppReactions.TriggerClass) !== null) { return; }
            if (this.Trigger === null) { this.Trigger = AppReactions.MakeTrigger(this.Doc as IRxDoc); }
            (this.Trigger as IRxNode).className = AppReactions.TriggerClass + AppReactions.SideOf(Bubble);
            Bubble.appendChild(this.Trigger as IRxNode);
            this.TriggerOn = Bubble;
            this.PlaceTrigger(Bubble);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.ShowTrigger");
        }
    };

    private PlaceTrigger = (Bubble: IRxNode): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Doc === null || this.Trigger === null || this.Trigger.style === undefined) { return; }
            const View = this.Doc.defaultView;
            if (View === null || View === undefined || typeof View.innerHeight !== "number"
                || typeof Bubble.getBoundingClientRect !== "function") { return; }
            const Box = Bubble.getBoundingClientRect();
            let Inset: number = 0;
            let Strip: IRxNode | null = null;
            if (this.Doc.body !== null) { Strip = this.Doc.body.querySelector("#wac-tasks"); }
            if (Strip !== null && typeof Strip.getBoundingClientRect === "function") { Inset = Strip.getBoundingClientRect().bottom; }
            const Top = String(AppReactRules.TriggerTop(Box.top, Box.bottom, View.innerHeight, Inset)) + "px";
            if (this.Trigger.style.top !== Top) { this.Trigger.style.top = Top; }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppReactions.PlaceTrigger"); }
    };

    private OnKeyboardOpen = (Bubble: IMessageElement): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true) { return; }
            const Offered = this.BubbleOf(Bubble);
            if (Offered !== null) { this.ShowTrigger(Offered); }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppReactions.OnKeyboardOpen"); }
    };

    private OnKeyboardClose = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { this.Close(); this.RestTrigger(); }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppReactions.OnKeyboardClose"); }
    };

    /** Navigation can place a bubble beneath a stationary pointer. Only movement offers its controls. */
    public OnMove = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.MouseMoved = true;
            this.OnOver(Ev);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.OnMove");
        }
    };

    public OnOut = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Coarse === true) { return; }
            const Next: unknown = (Ev as unknown as IRxEvent).relatedTarget;
            if (this.TriggerOn !== null && this.BubbleOf(Next) !== this.TriggerOn) {
                if (this.InGutter(Ev) === true) { this.CancelGrace(); }
                else { this.LeaveSoon(); }
            }
            if (Next === null || Next === undefined) {
                this.CancelGrace();
                this.RestTrigger();
                this.MouseMoved = false;
                this.CancelHold();
                this.Close();
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.OnOut");
        }
    };

    /** Keep this offer while the pointer crosses its visible side gutter, regardless of travel speed. */
    private InGutter = (Ev: Event): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Point = Ev as unknown as IRxEvent;
            const Bubble = this.TriggerOn;
            const Trigger = this.Trigger;
            if (this.Doc === null || Bubble === null || Trigger === null
                || typeof Point.clientX !== "number" || typeof Point.clientY !== "number"
                || Number.isFinite(Point.clientX) === false || Number.isFinite(Point.clientY) === false
                || typeof Bubble.getBoundingClientRect !== "function" || typeof Trigger.getBoundingClientRect !== "function") { return false; }
            const View = this.Doc.defaultView;
            if (View === null || View === undefined || typeof View.innerHeight !== "number") { return false; }
            const Box = Bubble.getBoundingClientRect();
            const Button = Trigger.getBoundingClientRect();
            let Left = Box.right;
            let Right = Button.right;
            if (Button.right <= Box.left) { Left = Button.left; Right = Box.left; }
            else if (Button.left < Box.right) { return false; }
            let Top = Math.max(0, Box.top);
            let Bottom = Math.min(View.innerHeight, Box.bottom);
            const Chat = Bubble.closest(".chat");
            if (Chat !== null && typeof Chat.getBoundingClientRect === "function") {
                const Clip = Chat.getBoundingClientRect();
                Left = Math.max(Left, Clip.left); Right = Math.min(Right, Clip.right);
                Top = Math.max(Top, Clip.top); Bottom = Math.min(Bottom, Clip.bottom);
            }
            let Strip: IRxNode | null = null;
            if (this.Doc.body !== null) { Strip = this.Doc.body.querySelector("#wac-tasks"); }
            if (Strip !== null && typeof Strip.getBoundingClientRect === "function") { Top = Math.max(Top, Strip.getBoundingClientRect().bottom); }
            return Point.clientX >= Math.max(0, Left) && Point.clientX <= Math.min(View.innerWidth, Right)
                && Point.clientY >= Top && Point.clientY <= Bottom;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppReactions.InGutter"); return false; }
    };

    private LeaveSoon = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.TriggerOn === null || this.Grace !== 0) { return; }
            this.GraceVersion++;
            this.Grace = this.Timer.Later(AppReactions.GraceMs,
                new AppReactLeave(this, this.TriggerOn, this.GraceVersion).Run);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppReactions.LeaveSoon"); }
    };

    private CancelGrace = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Grace !== 0) { this.Timer.Cancel(this.Grace); }
            this.Grace = 0; this.GraceVersion++;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppReactions.CancelGrace"); }
    };

    public OnGraceEnd = (Bubble: IRxNode, Version: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Version !== this.GraceVersion) { return; }
            this.Grace = 0;
            if (Bubble === this.TriggerOn) { this.RestTrigger(); }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppReactions.OnGraceEnd"); }
    };

    private RestTrigger = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.CancelGrace();
            if (this.Trigger === null || this.TriggerOn === null) { return; }
            this.TriggerOn.removeChild(this.Trigger);
            this.TriggerOn = null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.RestTrigger");
        }
    };

    /** A click: the ☺ opens the row, an emoji in the row is PICKED, anything else closes the row. */
    public OnClick = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true) { return; }
            const Node: IRxNode | null = AppReactions.NodeOf((Ev as unknown as IRxEvent).target);
            if (Node !== null && Node.closest("." + AppReactions.RowClass) !== null) {
                const Act: IRxNode | null = Node.closest("[" + AppMsgActionRules.ActAttr + "]");
                if (Act !== null) { this.PickAct(nz(Act.getAttribute(AppMsgActionRules.ActAttr))); return; }
                const Button: IRxNode | null = Node.closest("[" + AppReactions.EmojiAttr + "]");
                if (Button !== null) { this.Pick(nz(Button.getAttribute(AppReactions.EmojiAttr))); }
                return;
            }
            if (this.Now() - this.HeldAt < AppReactions.LiftMs) { return; }
            if (Node !== null && Node.closest("." + AppReactions.TriggerClass) !== null) {
                (Ev as unknown as IRxEvent).preventDefault();
                this.Open(this.BubbleOf(Node));
                if ((Ev as unknown as IRxEvent).detail === 0) { AppFocus.On(this.RowButtons[0]); }
                return;
            }
            this.Close();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.OnClick");
        }
    };

    /** A touch on an offerable bubble starts the hold. ⚠ Only a touch - a mouse has the ☺. */
    public OnDown = AppHold.Down(this, "AppReactions.OnDown", () => this.CancelHold());

    /** The finger lifted (or the gesture became a scroll) before the hold completed. */
    public OnUp = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.CancelHold();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.OnUp");
        }
    };

    /** Held long enough: open the row on that bubble. */
    public OnHeld = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Latch.Hold = 0;
            const Bubble: IRxNode | null = this.Latch.HoldOn as IRxNode | null;
            this.Latch.HoldOn = null;
            if (this.Stopped === true || Bubble === null) { return; }
            this.HeldAt = this.Now();
            this.Open(Bubble);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.OnHeld");
        }
    };

    /** ⚠ The phone's own long-press menu would cover the row - held on a bubble, it is ours. */
    public OnMenu = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Coarse === true && this.BubbleOf((Ev as unknown as IRxEvent).target) !== null) {
                (Ev as unknown as IRxEvent).preventDefault();
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.OnMenu");
        }
    };

    /**
     * ⭐ THE KEYBOARD WAY TO REACT (owner, 2026-10-20). On a focused bubble (`AppMessageKeyboard` makes them stops):
     * `r`, the Menu key or Shift+F10 open the row (`KeyOpens`) - ⛔ OPENING SENDS NOTHING. In the row: Left/Right/Home/End
     * move between its buttons (roving tabindex), Enter/Space press the focused button (the browser's own click ->
     * `OnClick` -> `Pick`, the ONE sender and latch), Esc closes the row and gives the focus back to the bubble.
     */
    public OnKey = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const E: IRxEvent = Ev as unknown as IRxEvent;
            const Key: string = nz(E.key);
            const Node: IRxNode | null = AppReactions.NodeOf(E.target);
            if (Key === "Escape") {
                const Back: IRxNode | null = this.KeyOrigin;
                const WasShown: boolean = this.Shown;
                this.Close();
                if (WasShown === true && Back !== null) { E.preventDefault(); AppFocus.On(Back as unknown as IFocusEl); }
                return;
            }
            if (this.Stopped === true || Node === null) { return; }
            if (this.Shown === true && this.Row !== null && Node.closest("." + AppReactions.RowClass) !== null) {
                if (E.ctrlKey === true || E.altKey === true || E.metaKey === true) { return; }
                const Live: IRxNode[] = this.LiveButtons();
                const Next: number = AppReactions.RoveTo(Live.indexOf(Node), Live.length, Key);
                if (Next < 0) { return; }
                E.preventDefault();
                this.Rove(Live, Next);
                return;
            }
            if (AppReactions.KeyOpens(E) === false) { return; }
            const Bubble: IRxNode | null = this.BubbleOf(Node);
            // ⛔ Only the bubble ITSELF (the keyboard stop) - never a key pressed in something inside or beside it.
            if (Bubble === null || Bubble !== Node) { return; }
            E.preventDefault();
            this.Open(Bubble);
            if (this.Shown === false) { return; }
            this.KeyOrigin = Bubble;
            const Live: IRxNode[] = this.LiveButtons();
            let Start: number = 0;
            for (let At: number = 0; At < Live.length; At = At + 1) {
                if (Live[At].getAttribute("aria-pressed") === "true") { Start = At; break; }
            }
            this.Rove(Live, Start);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.OnKey");
        }
    };

    /**
     * Does this press open the row on a focused bubble? `r` alone, the Menu key alone, or Shift+F10. ⛔ Never with another
     * modifier, never a held key (auto-repeat), never while an input method composes. ERROR-SUPPRESSED-JUSTIFIED: pure.
     */
    public static KeyOpens = (E: IRxEvent): boolean => {
        if (E.repeat === true || E.isComposing === true || E.keyCode === 229) { return false; }
        if (E.ctrlKey === true || E.altKey === true || E.metaKey === true) { return false; }
        const Key: string = nz(E.key);
        if (Key === "F10") { return E.shiftKey === true; }
        if (E.shiftKey === true) { return false; }
        return Key === "r" || Key === "ContextMenu";
    };

    /** The row's button a roving key moves to (-1 = not a roving key). Wraps like a toolbar. ERROR-SUPPRESSED-JUSTIFIED: pure. */
    public static RoveTo = (At: number, Count: number, Key: string): number => {
        if (Count <= 0) { return -1; }
        const From: number = At < 0 ? 0 : At;
        if (Key === "ArrowRight" || Key === "ArrowDown") { return (From + 1) % Count; }
        if (Key === "ArrowLeft" || Key === "ArrowUp") { return (From - 1 + Count) % Count; }
        if (Key === "Home") { return 0; }
        if (Key === "End") { return Count - 1; }
        return -1;
    };

    /** The row's buttons a key can reach: the six faces and the actions not hidden for this bubble. */
    private LiveButtons = (): IRxNode[] => {
        const Out: IRxNode[] = this.RowButtons.slice();
        for (const Button of this.ActButtons) { if (Button.getAttribute("hidden") === null) { Out.push(Button); } }
        return Out;
    };

    /** Roving tabindex: one button is the row's stop (0), the rest -1; the focus goes to it. */
    private Rove = (Live: IRxNode[], To: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            for (let At: number = 0; At < Live.length; At = At + 1) {
                const Tab: string = At === To ? "0" : "-1";
                if (Live[At].getAttribute("tabindex") !== Tab) { Live[At].setAttribute("tabindex", Tab); }
            }
            if (Live[To] !== undefined) { AppFocus.On(Live[To] as unknown as IFocusEl); }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppReactions.Rove"); }
    };

    /** The row is placed in the viewport, so a scroll would leave it floating beside nothing. */
    public OnScroll = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.CancelHold();
            this.Close();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.OnScroll");
        }
    };

    private CancelHold = (): void => {
        AppHold.Cancel(this.Latch, true, "AppReactions.CancelHold");
    };

    /** Show the row over `Bubble`, the owner's current emoji marked. ⚠ Opening sends NOTHING. */
    public Open = (Bubble: IRxNode | null): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Bubble === null || this.Doc === null || this.Stopped === true) { return; }
            const Doc: IRxDoc = this.Doc as IRxDoc;
            if (Doc.body === null) { return; }
            if (this.Row === null) { this.Row = this.MakeRow(Doc); }
            const Row: IRxNode = this.Row as IRxNode;
            this.MarkCurrent(AppReactions.MineOf(Bubble));
            this.MarkActs(AppReactions.SideOf(Bubble) === " me");
            (Doc.body as IRxNode).appendChild(Row);
            this.Shown = true;
            this.Target = Bubble;
            this.Place(Row, Bubble);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.Open");
        }
    };

    private Place = (Row: IRxNode, Bubble: IRxNode): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            let Width: number = 1024;
            if (this.Doc !== null) {
                const View: IRxView | null | undefined = (this.Doc as IRxDoc).defaultView;
                if (View !== null && View !== undefined) { Width = (View as IRxView).innerWidth; }
            }
            if (typeof Bubble.getBoundingClientRect !== "function") { return; }
            const Box: IRxRect = (Bubble.getBoundingClientRect as () => IRxRect)();
            // ⛔ A bubble that is a task's source carries "Mark done" on its edge: the row keeps clear of it (`HangPx`).
            let Hang: number = 0;
            if (nz(Bubble.getAttribute(AppReactRules.HangAttr)).length > 0) { Hang = AppReactRules.HangPx; }
            // ⚠ The row's REAL size where it can be measured: on a phone its buttons are 44px (`AppFrameA11y`), so the
            // row is taller and wider than the constants below - placed by them it reached 6px into the band under it.
            let RowW: number = AppReactions.RowWidthPx;
            let RowH: number = AppReactions.RowHeightPx;
            if (typeof Row.getBoundingClientRect === "function") {
                const Own: IRxRect = (Row.getBoundingClientRect as () => IRxRect)();
                if (Own.right - Own.left > 0 && Own.bottom - Own.top > 0) { RowW = Own.right - Own.left; RowH = Own.bottom - Own.top; }
            }
            const At: AppReactPlace = AppReactRules.PlaceRow(Box.left, Box.top, Box.right, Box.bottom, Width,
                RowW, RowH, AppReactions.SideOf(Bubble) === " me", Hang);
            if (Row.style === undefined) { return; }
            (Row.style as IRxStyle).left = String(Math.round(At.Left)) + "px";
            (Row.style as IRxStyle).top = String(Math.round(At.Top)) + "px";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.Place");
        }
    };

    /** The row's measured size in the stylesheet below (6 x 38px buttons + padding). */
    public static readonly RowWidthPx: number = 244;
    public static readonly RowHeightPx: number = 46;

    public Close = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Target = null;
            this.KeyOrigin = null;
            if (this.Shown === false || this.Row === null || this.Doc === null || (this.Doc as IRxDoc).body === null) { return; }
            this.Shown = false;
            ((this.Doc as IRxDoc).body as IRxNode).removeChild(this.Row as IRxNode);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.Close");
        }
    };

    // ── Sending ──────────────────────────────────────────────────────────────────────────────

    /**
     * ⛔ THE ONLY WAY A REACTION LEAVES: a click on one of the row's buttons (`OnClick`). The emoji
     * must be one of the row's; the owner's current one sends "" (take it off).
     */
    public Pick = (Emoji: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Bubble: IRxNode | null = this.Target;
            const Back: IRxNode | null = this.KeyOrigin;
            this.Close();
            if (Back !== null) { AppFocus.On(Back as unknown as IFocusEl); }
            if (Bubble === null || this.Stopped === true || AppReactRules.Allowed(Emoji) === false) { return; }
            const Mid: string = nz(Bubble.getAttribute("data-mid"));
            if (this.Pending[Mid] !== undefined) { this.Host.Say(AppReactRules.BusyWhy, false); return; }
            const Next: string = AppReactRules.Toggle(AppReactions.MineOf(Bubble), Emoji);
            this.Pending[Mid] = this.Draw(Bubble, Next);
            const Why: string = this.Host.React(Mid, Next, new AppReactSend(this, Mid));
            // REUSE-JUSTIFIED: `Why` is the service's refusal sentence (validation the owner can act on - fate 1, as for every send); a real fault goes to the handler in the catch.
            if (Why.length > 0) { this.Revert(Mid); this.Host.Say(Why, true); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.Pick");
        }
    };

    /**
     * ⭐ Reply / Edit / Delete clicked (`OnClick`): the row closes and the host sets the composer's mode on this bubble.
     * ⛔ NOTHING IS SENT HERE. Edit and Delete only on the owner's own bubble (the service re-checks).
     */
    public PickAct = (Kind: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Bubble: IRxNode | null = this.Target;
            this.Close();
            if (Bubble === null || this.Stopped === true || typeof this.Host.Act !== "function") { return; }
            const Own: boolean = AppReactions.SideOf(Bubble) === " me";
            if (AppMsgActionRules.OfferedFor(Own).indexOf(Kind) < 0) { return; }
            let Words: string = "";
            const Txt: IRxNode | null = Bubble.querySelector(".txt");
            if (Txt !== null) { Words = nz(Txt.textContent); }
            (this.Host.Act as NonNullable<IRxHost["Act"]>).call(this.Host, Kind, nz(Bubble.getAttribute("data-mid")), Words);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.PickAct");
        }
    };

    /** Edit / Delete shown only on the owner's own bubble - an attribute changes; the row is never rebuilt. */
    private MarkActs = (Own: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            for (const Button of this.ActButtons) {
                const Kind: string = nz(Button.getAttribute(AppMsgActionRules.ActAttr));
                if (AppMsgActionRules.OfferedFor(Own).indexOf(Kind) >= 0) { Button.removeAttribute("hidden"); }
                else { Button.setAttribute("hidden", ""); }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.MarkActs");
        }
    };

    /**
     * The receipt's verdict for the reaction on `Mid` (`AppMain`'s poll). Sent: the pill is final.
     * Unconfirmed: kept, and the owner is told to check the phone. Refused: the ORIGINAL pill is back.
     */
    public Settle = (Mid: string, Sent: boolean, Unconfirmed: boolean, Why: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true || this.Pending[Mid] === undefined) { return; }
            if (Sent === true) { this.Confirm(Mid); return; }
            if (Unconfirmed === true) { this.Confirm(Mid); this.Host.Say(AppReactRules.UnconfirmedWhy, false); return; }
            this.Refused(Mid, Why);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.Settle");
        }
    };

    /** The page stopped waiting: the pill stays (it is still queued), no longer faded. */
    public GaveUp = (Mid: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.ConfirmIfPending(Mid) === true) { this.Host.Say(AppReactRules.StillWaitingWhy, false); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.GaveUp");
        }
    };

    /** `/send` refused it, or the receipt did: the original pill, and the reason under the box. */
    public Refused = (Mid: string, Why: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true || this.Pending[Mid] === undefined) { return; }
            this.Revert(Mid);
            // REUSE-JUSTIFIED: a refusal from /send or WhatsApp is an expected answer the owner acts on (fate 1, like the composer's "Not sent"); reporting it would latch the page gate.
            this.Host.Say(AppReactions.NotSentWhy(Why), true);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.Refused");
        }
    };

    /** Is a reaction on `Mid` still on its way? For the suite, and for nothing else. */
    public IsPending = (Mid: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return this.Pending[Mid] !== undefined;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.IsPending");
            return false;
        }
    };

    private Confirm = (Mid: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const One: AppReactPending = this.Pending[Mid];
            delete this.Pending[Mid];
            if (One.NewPill !== null) {
                (One.NewPill as IRxNode).className = AppReactions.Classes((One.NewPill as IRxNode).className, "", AppReactions.PendingClass);
            }
            this.Tell(Mid, true);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.Confirm");
        }
    };

    /** The reaction on `Mid` settled - say so to whoever listens (`OnSettled`), after the bubble is as it will stay. */
    private Tell = (Mid: string, Kept: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Hear: ((Mid: string, Kept: boolean) => void) | null = this.OnSettled;
            if (Hear !== null) { Hear(Mid, Kept); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.Tell");
        }
    };

    /**
     * ⭐ LIVE (`AppLiveReact`): the emoji of the owner's reaction still on its way for `Mid` ("" = taking it off),
     * or null when none is. ⛔ It reads; nothing is sent.
     */
    public PendingNext = (Mid: string): string | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const One: AppReactPending | undefined = this.Pending[Mid];
            if (this.Stopped === true || One === undefined) { return null; }
            return One.Next;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.PendingNext");
            return null;
        }
    };

    /**
     * ⭐ LIVE: WhatsApp's own echo of the reaction on its way for `Mid` arrived through `/live` - it WENT. The pill is
     * final now (the later receipt finds nothing pending and changes nothing). ⛔ It confirms; nothing is sent.
     */
    /** The settle `GaveUp` and `Echoed` share: still pending? then confirm it, and say so. */
    private ConfirmIfPending = (Mid: string): boolean => {
        if (this.Stopped === true || this.Pending[Mid] === undefined) { return false; }
        this.Confirm(Mid);
        return true;
    };

    public Echoed = (Mid: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.ConfirmIfPending(Mid);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.Echoed");
        }
    };

    /** Put the bubble back EXACTLY as the page drew it - the original pill node, the original class. */
    private Revert = (Mid: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const One: AppReactPending = this.Pending[Mid];
            delete this.Pending[Mid];
            if (One === undefined || One.Bubble === null) { return; }
            const Bubble: IRxNode = One.Bubble as IRxNode;
            if (One.NewPill !== null) { Bubble.removeChild(One.NewPill as IRxNode); }
            if (One.OldPill !== null) { Bubble.appendChild(One.OldPill as IRxNode); }
            Bubble.className = One.OldClass;
            this.Tell(Mid, false);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.Revert");
        }
    };

    /**
     * The optimistic pill: the page's own pill is taken off (kept for a revert) and a new one - the
     * others' reactions plus the owner's `Next`, faded - goes in its place. `Next` "" and nobody else
     * reacting: no pill, and the bubble loses its badge margin (`rxd`).
     */
    private Draw = (Bubble: IRxNode, Next: string): AppReactPending => {
        if (getCoreUtils().KErrorMode === true) { return new AppReactPending(); }
        try {
            const Out: AppReactPending = new AppReactPending();
            Out.Bubble = Bubble;
            Out.Next = nz(Next);
            Out.OldClass = Bubble.className;
            Out.OldPill = Bubble.querySelector(".rx");
            let Others: string[] = [];
            let OthersWho: string = "";
            if (Out.OldPill !== null) {
                Others = AppReactRules.OthersOf((Out.OldPill as IRxNode).getAttribute(AppReactRules.OthersAttr));
                OthersWho = nz((Out.OldPill as IRxNode).getAttribute(AppReactRules.OthersWhoAttr));
                Bubble.removeChild(Out.OldPill as IRxNode);
            }
            Out.NewPill = this.PillOf(Others, OthersWho, Next);
            if (Out.NewPill === null) { Bubble.className = AppReactions.Classes(Bubble.className, "", "rxd"); return Out; }
            Bubble.className = AppReactions.Classes(Bubble.className, "rxd", "");
            Bubble.appendChild(Out.NewPill as IRxNode);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.Draw");
            return new AppReactPending();
        }
    };

    /**
     * ⭐ THE ONE PILL FILLER: title, aria-label, mine / others attributes, the faces (`AppIcons.BuildFaces`) and the
     * count - on a span the caller made and classed. Used by `PillOf` (the pending pill) and `AppLiveReact.PillNode`.
     */
    public static FillPill = (Doc: IRxDoc, Pill: IRxNode, Shape: AppReactPill): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            Pill.setAttribute("title", Shape.Title);
            Pill.setAttribute("aria-label", Shape.Title);
            if (Shape.Mine.length > 0) { Pill.setAttribute(AppReactRules.MineAttr, Shape.Mine); }
            if (Shape.Others.length > 0) {
                Pill.setAttribute(AppReactRules.OthersAttr, Shape.Others.join(" "));
                Pill.setAttribute(AppReactRules.OthersWhoAttr, Shape.OthersWho);
            }
            AppIcons.BuildFaces(Doc, Pill, Shape.Emoji);
            if (Shape.Count > 1) {
                const Count: IRxNode = Doc.createElement("i");
                Count.textContent = String(Shape.Count);
                Pill.appendChild(Count);
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.FillPill");
        }
    };

    /** A pill shaped like `ReactionBook.Html`'s, marked pending; null when nobody's reaction stands. */
    private PillOf = (Others: string[], OthersWho: string, Mine: string): IRxNode | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Shown: { Emoji: string[]; Count: number } = AppReactRules.PillOf(Others, Mine);
            if (Shown.Emoji.length === 0 || this.Doc === null) { return null; }
            const Doc: IRxDoc = this.Doc as IRxDoc;
            const Pill: IRxNode = Doc.createElement("span");
            Pill.className = "rx " + AppReactions.PendingClass;
            const Shape: AppReactPill = new AppReactPill();
            Shape.Emoji = Shown.Emoji;
            Shape.Count = Shown.Count;
            Shape.Mine = Mine;
            Shape.Others = Others;
            Shape.OthersWho = OthersWho;
            Shape.Title = AppReactRules.TitleOf(Mine, OthersWho);
            AppReactions.FillPill(Doc, Pill, Shape);
            return Pill;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.PillOf");
            return null;
        }
    };

    // ── Small helpers ────────────────────────────────────────────────────────────────────────

    /** The owner's standing reaction on a bubble - the pill's `data-mine` - or "". */
    public static MineOf = (Bubble: IRxNode): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Pill: IRxNode | null = Bubble.querySelector(".rx");
            if (Pill === null) { return ""; }
            return nz((Pill as IRxNode).getAttribute(AppReactRules.MineAttr));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.MineOf");
            return "";
        }
    };

    /**
     * An event target as a node that can `closest`, or null (a text node, the document).
     * DOCUMENTED EXCEPTION: a type probe over a value in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: null IS the answer for anything that is not an element.
     */
    private static NodeOf = (Target: unknown): IRxNode | null => {
        return AppNodeOf.Read<IRxNode>(Target);
    };

    /**
     * " me" for the owner's own bubble, " them" otherwise - which side the ☺ sits on.
     * DOCUMENTED EXCEPTION: reads one class list in hand. ERROR-SUPPRESSED-JUSTIFIED: pure.
     */
    private static SideOf = (Bubble: IRxNode): string => {
        if ((" " + nz(Bubble.className) + " ").indexOf(" me ") >= 0) { return " me"; }
        return " them";
    };

    /**
     * A class list with `Add` added and `Drop` removed (either may be "").
     * DOCUMENTED EXCEPTION: pure string arithmetic. ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw.
     */
    public static Classes = (List: string, Add: string, Drop: string): string => {
        const Out: string[] = AppClassList.Tokens(nz(List)).filter((One: string) => One !== Drop && One !== Add);
        if (Add.length > 0) { Out.push(Add); }
        return Out.join(" ");
    };

    /**
     * "Reaction not sent: <why>". ⚠ `/send` words its refusals "not sent: <why>" (`SendRules`), so that
     * prefix is dropped - the first render read "Reaction not sent: not sent: ...".
     * DOCUMENTED EXCEPTION: pure composition. ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw.
     */
    public static NotSentWhy = (Why: string): string => {
        const Bare: string = nz(Why).trim().replace(/^not sent:\s*/i, "");
        if (Bare.length === 0) { return "Reaction not sent."; }
        return "Reaction not sent: " + Bare;
    };

    /** The owner's current emoji is pressed in the row. ⛔ Compared in a loop - an emoji never enters a selector. */
    private MarkCurrent = (Mine: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            for (const Button of this.RowButtons) {
                const Emoji: string = nz(Button.getAttribute(AppReactions.EmojiAttr));
                let On: boolean = false;
                if (Mine.length > 0 && AppReactRules.Plain(Mine) === AppReactRules.Plain(Emoji)) { On = true; }
                Button.setAttribute("aria-pressed", String(On));
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.MarkCurrent");
        }
    };

    private static MakeTrigger = (Doc: IRxDoc): IRxNode => {
        if (getCoreUtils().KErrorMode === true) { return Doc.createElement("button"); }
        try {
            const Button: IRxNode = Doc.createElement("button");
            Button.setAttribute("type", "button");
            Button.setAttribute("title", "React");
            Button.setAttribute("aria-label", "React to this message");
            const Face: IRxNode | null = AppIcons.Build(Doc, AppIcons.React, 0) as IRxNode | null;
            if (Face !== null) { Button.appendChild(Face as IRxNode); }
            return Button;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.MakeTrigger");
            return Doc.createElement("button");
        }
    };

    /** The row: six buttons, each carrying its emoji in an attribute (never parsed from its text). */
    private MakeRow = (Doc: IRxDoc): IRxNode => {
        if (getCoreUtils().KErrorMode === true) { return Doc.createElement("div"); }
        try {
            const Row: IRxNode = Doc.createElement("div");
            Row.className = AppReactions.RowClass;
            Row.setAttribute("role", "toolbar");
            Row.setAttribute("aria-label", "React");
            const Buttons: IRxNode[] = [];
            for (let At: number = 0; At < AppReactRules.Emoji.length; At = At + 1) {
                const Button: IRxNode = Doc.createElement("button");
                Button.setAttribute("type", "button");
                Button.setAttribute(AppReactions.EmojiAttr, AppReactRules.Emoji[At]);
                Button.setAttribute("title", AppReactRules.Names[At]);
                Button.setAttribute("aria-label", AppReactRules.Names[At]);
                // ⭐ OUR ICON, NOT THE GLYPH: the emoji stays in the attribute (the wire value) only.
                const Icon: AppIcon | null = AppIcons.ForEmoji(AppReactRules.Emoji[At]);
                if (Icon !== null) {
                    const Face: IRxNode | null = AppIcons.Build(Doc, Icon as AppIcon, 0) as IRxNode | null;
                    if (Face !== null) { Button.appendChild(Face as IRxNode); }
                }
                Row.appendChild(Button);
                Buttons.push(Button);
            }
            this.AddActs(Doc, Row, Buttons);
            this.RowButtons = Buttons;
            return Row;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.MakeRow");
            return Doc.createElement("div");
        }
    };

    /** ⭐ The row's Reply / Edit / Delete buttons (text labels, keyboard stops) - only when the host can act. */
    private AddActs = (Doc: IRxDoc, Row: IRxNode, Buttons: IRxNode[]): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (typeof this.Host.Act !== "function") { return; }
            for (const Kind of AppMsgActionRules.OfferedFor(true)) {
                const Button: IRxNode = Doc.createElement("button");
                Button.setAttribute("type", "button");
                Button.className = "wac-rxact";
                Button.setAttribute(AppMsgActionRules.ActAttr, Kind);
                Button.textContent = AppMsgActionRules.Labels[Kind];
                Row.appendChild(Button);
                // Not in `Buttons` (`RowButtons`): those are the faces, whose `aria-pressed` says the owner's reaction.
                this.ActButtons.push(Button);
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactions.AddActs");
        }
    };

    /**
     * The stylesheet, in the page's own colours (`--inbg`, `--dim`) - linked into every contact frame by
     * `AppMain.Frame`. DOCUMENTED EXCEPTION: a constant stylesheet. ERROR-SUPPRESSED-JUSTIFIED: returns a literal.
     */
    public static Css = (): string => {
        // ⭐ `AppIcons.Css` FIRST: the reaction colour tokens for the light AND the dark theme, and the
        // icon box. The buttons centre an SVG, never a font glyph.
        return AppIcons.Css()
            // ⭐ A BUBBLE IS AS TALL AS ITS WORDS (business look wave B, 2026-10-13). The 44px floor is a TOUCH target (the
            // hold that opens the reactions), so it holds on a coarse pointer and on a phone-width frame - not on a desktop
            // with a mouse, where it made every one-line message 44px tall (and never in three panes: `AppFrameWide`).
            + ".b[data-mid]{min-width:44px}"
            + "@media(pointer:coarse){.b[data-mid]{min-height:44px}}"
            + "@media(max-width:620px){html" + AppFrameWide.Not + " .b[data-mid]{min-height:44px}}"
            + ".b[data-mid]:focus-visible{outline:2px solid var(--acc,#095495);outline-offset:3px}"
            + ".wac-rxbtn{position:absolute;top:50%;transform:translateY(-50%);width:28px;height:28px;"
            // ⭐ The business look (2026-10-13): a square icon button with an edge (it was a round, shadowed disc).
            + "box-sizing:border-box;border:1px solid var(--line,#e3e6ec);border-radius:6px;background:var(--inbg,#fff);"
            + "color:var(--dim,#5c6370);display:inline-flex;align-items:center;justify-content:center;padding:0;cursor:pointer;"
            + "opacity:.9;z-index:2}.wac-rxbtn svg{width:19px;height:19px}"
            + ".wac-rxbtn::before{content:'';position:absolute;inset:-8px}"
            + ".wac-rxbtn.them{right:-28px}.wac-rxbtn.me{left:-28px}.wac-rxbtn:hover{opacity:1}"
            // The row floats over the messages: the one place here a shadow stays. A rectangle, not a capsule.
            + ".wac-rxrow{position:fixed;z-index:50;display:flex;gap:2px;padding:4px;border-radius:8px;"
            + "border:1px solid var(--line,#e3e6ec);background:var(--inbg,#fff);box-shadow:0 4px 16px rgba(16,20,28,.2)}"
            + ".wac-rxrow button{width:38px;height:38px;border:0;border-radius:6px;background:transparent;"
            + "display:inline-flex;align-items:center;justify-content:center;padding:0;cursor:pointer;transition:transform .12s}"
            + ".wac-rxrow button svg{width:26px;height:26px}"
            + ".wac-rxrow button:hover,.wac-rxrow button:focus-visible{transform:scale(1.18);outline:0}"
            + ".wac-rxrow button[aria-pressed=true]{background:rgba(11,20,26,.1)}"
            + ".b .rx.wac-pending{opacity:.55}"
            // (2026-10-14) Reply / Edit / Delete: text buttons after the faces, a hairline between; `hidden` honoured.
            + ".wac-rxrow button.wac-rxact{width:auto;padding:0 8px;font:inherit;font-size:13px;color:var(--ink,#2b2f36)}"
            + ".wac-rxrow button.wac-rxact[hidden]{display:none}"
            // The faces fill the first line; the actions wrap under them when the frame is narrow (never off-screen).
            + ".wac-rxrow{flex-wrap:wrap;max-width:min(256px,calc(100vw - 16px))}"
            + ".wac-rxrow button.wac-rxact:hover,.wac-rxrow button.wac-rxact:focus-visible{transform:none;"
            + "background:rgba(11,20,26,.08);outline:2px solid var(--accent,#1f6feb)}"
            + "@media(prefers-color-scheme:dark){.wac-rxrow button[aria-pressed=true]{background:rgba(255,255,255,.14)}}";
    };
}

/**
 * Hears how ONE reaction's request went (`IComposerSent`): accepted by `/send` (the receipt is still
 * to come - the pill stays faded) or refused (the original pill comes back). A class, not a closure.
 */
/** One leave timer, retaining its original bubble and arm so a cancelled callback cannot hide a new offer. */
class AppReactLeave {
    private Owner: AppReactions;
    private Bubble: IRxNode;
    private Version: number;
    public constructor(Owner: AppReactions, Bubble: IRxNode, Version: number) {
        this.Owner = Owner; this.Bubble = Bubble; this.Version = Version;
    }
    public Run = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { this.Owner.OnGraceEnd(this.Bubble, this.Version); }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppReactLeave.Run"); }
    };
}

export class AppReactSend implements IComposerSent {
    private Owner: AppReactions;
    private Mid: string;

    public constructor(Owner: AppReactions, Mid: string) {
        this.Owner = Owner;
        this.Mid = Mid;
    }

    /** ⚠ QUEUED IS NOT SENT: the pill stays faded until the receipt (`AppReactions.Settle`). */
    public Queued = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            return;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactSend.Queued");
        }
    };

    public Failed = (Why: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Owner.Refused(this.Mid, Why);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactSend.Failed");
        }
    };
}

/** The long-press state shared by the reaction row and the task-frame button. */
export class AppHoldLatch {
    public readonly HoldMs: number;
    public readonly Clock: IRxLater;
    public Hold: number = 0;
    public HoldOn: unknown | null = null;
    public constructor(HoldMs: number, Clock: IRxLater) { this.HoldMs = HoldMs; this.Clock = Clock; }
}

/** The host a hold view gives Down: stop flag, bubble lookup, latch, held step. */
export interface IAppHoldDown {
    readonly Stopped: boolean;
    BubbleOf(Target: unknown): unknown;
    readonly Latch: AppHoldLatch;
    OnHeld(): void;
}

/** Shared long-press hold start/cancel for the reaction row and the task-frame button. */
export class AppHold {
    /** Cancel a pending hold. `ClearNode` also drops the latched node: the reaction row does, the task frame's finger-lift does not. */
    public static Cancel = (Latch: AppHoldLatch, ClearNode: boolean, Label: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Latch.Hold !== 0) { Latch.Clock.Cancel(Latch.Hold); }
            Latch.Hold = 0;
            if (ClearNode === true) { Latch.HoldOn = null; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, Label);
        }
    };

    /** Start a hold on a touch over a bubble: latch the bubble and arm the timer. `Before` is the caller's own pre-step (cancel any pending hold first), run under its own label. */
    public static Start = (Stopped: boolean, Ev: Event, BubbleOf: (Target: unknown) => unknown, Latch: AppHoldLatch, OnHeld: () => void, Label: string, Before?: () => void): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Before !== undefined) { Before(); }
            if (Stopped === true || nz((Ev as unknown as IRxEvent).pointerType) !== "touch") { return; }
            const Bubble: unknown = BubbleOf((Ev as unknown as IRxEvent).target);
            if (Bubble === null) { return; }
            Latch.HoldOn = Bubble;
            Latch.Hold = Latch.Clock.Later(Latch.HoldMs, OnHeld);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, Label);
        }
    };

    /** The OnDown arrow both views attach verbatim: routes the touch to Start under the caller's label and pre-step. */
    public static Down = (Host: IAppHoldDown, Label: string, Before?: () => void): (Ev: Event) => void => {
        return (Ev: Event): void => { AppHold.Start(Host.Stopped, Ev, Host.BubbleOf, Host.Latch, Host.OnHeld, Label, Before); };
    };
}

/** The frame-document load both views share: the guard head and the six pointer listeners. */
export class AppDocListen {
    /** The stopped/null/already-claimed head; null means "nothing to load". */
    public static FrameOf = (Stopped: boolean, Frame: { contentDocument: unknown } | null, DocSet: boolean): unknown => {
        if (Stopped === true || Frame === null || Frame.contentDocument === null) { return null; }
        if (DocSet === true) { return null; }
        return Frame.contentDocument;
    };

    /** mouseover/move/out + pointerdown/up/cancel - the six both attach verbatim. */
    public static Pointer = (Doc: { addEventListener(Type: string, Fn: (Ev: Event) => void): void },
                             Over: (Ev: Event) => void, Move: (Ev: Event) => void, Out: (Ev: Event) => void,
                             Down: (Ev: Event) => void, Up: (Ev: Event) => void): void => {
        Doc.addEventListener("mouseover", Over);
        Doc.addEventListener("mousemove", Move);
        Doc.addEventListener("mouseout", Out);
        Doc.addEventListener("pointerdown", Down);
        Doc.addEventListener("pointerup", Up);
        Doc.addEventListener("pointercancel", Up);
    };

    /** The OnLoad shell: gate, the shared head, then the caller's own per-document load under its label. */
    public static Loaded = (Stopped: boolean, Frame: { contentDocument: unknown } | null, DocSet: boolean,
                            OnDoc: (Raw: unknown) => void, Label: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Raw: unknown = AppDocListen.FrameOf(Stopped, Frame, DocSet);
            if (Raw === null) { return; }
            OnDoc(Raw);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, Label);
        }
    };
}
